import React, { useState, useEffect } from "react";
import http from "../../../../services/http";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Card, Col, Form, Input, Row, Select } from "antd";
import DocEditor from "../../../../components/doc-editor";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { IMAGE_HOST } from "../../../../constant";

const DEFAULT_AUTHOR_NAME = "Grayson Rothchild";
const DEFAULT_AUTHOR_IMAGE = IMAGE_HOST + "blog/authors/default.png";

function BlogForm() {
  // STATES
  const [content, setContent] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [authorImage, setAuthorImage] = useState(null);
  const [authorImagePreview, setAuthorImagePreview] = useState(DEFAULT_AUTHOR_IMAGE);
  const [pageLoader, setpageLoader] = useState(false);
  const [isLoading, setLoading] = useState(false);

  // CONST VALS
  const history = useHistory();
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const isEdit = pathname.includes("edit");
  const { id } = useParams();

  // HELPERS
  const getBlogHelper = async () => {
    setpageLoader(true);
    try {
      const { data } = await http.get(`admin-blog/${id}`);
      setpageLoader(false);
      form.setFieldsValue({
        ...data,
        author_name: data.author_name || DEFAULT_AUTHOR_NAME,
        status: data.is_published ? "published" : "draft",
        keywords: data.keywords ? data.keywords.join(", ") : "",
      });
      setContent(data.content);
      if (data.thumbnail) setThumbnailPreview(IMAGE_HOST + data.thumbnail);
      setAuthorImagePreview(
        data.author_image ? IMAGE_HOST + data.author_image : DEFAULT_AUTHOR_IMAGE
      );
    } catch (error) {
      toast.error(error.response.data.error);
    }
  };

  const createBlogHelper = async (payload) => {
    const { data } = await http.post("admin-blog", payload);
    return data;
  };

  const updateBlogHelper = async (payload) => {
    const { data } = await http.put(`admin-blog/${id}`, payload);
    return data;
  };

  // HANDLERS
  const setContentHandler = (value) => {
    setContent(value);
  };
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    setThumbnail(file);
    setThumbnailPreview(URL.createObjectURL(file));
  };
  const removeThumbnailHandler = () => {
    setThumbnail(null);
    setThumbnailPreview(null);
  };
  const handleAuthorImageChange = (e) => {
    const file = e.target.files[0];
    setAuthorImage(file);
    setAuthorImagePreview(URL.createObjectURL(file));
  };

  const submitHandler = (values) => {
    if (content.length < 100) {
      toast.error("Content should be at least 100 characters");
      return;
    }
    if (!thumbnail && !thumbnailPreview) {
      toast.error("Thumbnail is required");
      return;
    }

    setLoading(true);
    const slug = values.title.toLowerCase().replace(/ /g, "-");
    const payload = new FormData();
    payload.append("title", values.title);
    payload.append("slug", values.slug || slug);
    payload.append("category", values.category);
    payload.append("description", values.description);
    payload.append("author_name", values.author_name || DEFAULT_AUTHOR_NAME);
    if (values.keywords) {
      console.log("keywords", values.keywords);
      const keywords = values.keywords.split(",");
      payload.append("keywords", `{${keywords}}`);
    }
    if (thumbnail) payload.append("thumbnail", thumbnail);
    if (authorImage) payload.append("author_image", authorImage);
    payload.append("content", content);
    payload.append("is_published", values.status === "published" ? 1 : 0);

    const request = isEdit ? updateBlogHelper : createBlogHelper;
    request(payload)
      .then(({ data }) => {
        toast.success(isEdit ? "Blog updated successfully" : "Blog created successfully");
        history.push("/blogs");
      })
      .catch(({ response }) => toast.error(response.data.error ?? "Something went wrong"))
      .finally(() => setLoading(false));
  };

  // HOOKS
  useEffect(() => {
    if (isEdit) {
      getBlogHelper();
    }
  }, [isEdit, id]);

  return (
    <div className="blog-form-wrapper">
      <h2 className="page-title">{isEdit ? "Edit Blog" : "Add Blog"}</h2>
      <Card bordered={false}>
        {pageLoader ? (
          <div className="loader-wrapper">
            <BeatLoader color="#605bff" size={14} />
          </div>
        ) : (
          <Form
            onFinish={submitHandler}
            form={form}
            initialValues={{ list: "default", author_name: DEFAULT_AUTHOR_NAME }}
          >
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <label htmlFor="title">Title</label>
                <Form.Item
                  name="title"
                  rules={[
                    { required: true, whitespace: true, message: "Title is required" },
                    {
                      max: 200,
                      message: "Title should not exceed 200 characters",
                    },
                  ]}
                >
                  <Input placeholder="Enter blog title" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <label htmlFor="title">Slug</label>
                <Form.Item name="slug">
                  <Input placeholder="Enter blog slug or leave blank for auto generation" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <label htmlFor="category">Category</label>
                <Form.Item name="category">
                  <Input id="category" placeholder="Enter category" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <label htmlFor="status">Status</label>
                <Form.Item
                  name="status"
                  rules={[{ required: true, message: "Status is required" }]}
                >
                  <Select placeholder="Select status">
                    <Select.Option value="published">Published</Select.Option>
                    <Select.Option value="draft">Draft</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <label htmlFor="keywords">Keywords</label>
                <Form.Item name="keywords">
                  <Input
                    id="keywords"
                    placeholder="Enter keywords separated by commas for SEO"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <label htmlFor="description">Description</label>
                <Form.Item
                  name="description"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Description is required",
                    },
                    {
                      max: 400,
                      message: "Description should not exceed 400 characters",
                    },
                  ]}
                >
                  <Input.TextArea placeholder="Enter blog description" rows={5} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <label htmlFor="author_name">Author</label>
                <div className="author-fields">
                  <div className="author-image-wrapper">
                    <img src={authorImagePreview} alt="Author" />
                    <label htmlFor="author_image" className="upload-btn">
                      Change
                    </label>
                    <input
                      type="file"
                      id="author_image"
                      accept="image/*"
                      onChange={handleAuthorImageChange}
                    />
                  </div>
                  <Form.Item name="author_name" className="author-name-field">
                    <Input id="author_name" placeholder="Enter author name" />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={24} md={6}>
                <label htmlFor="thumbnail">Thumbnail</label>
                <div className="thumbnail-wrapper">
                  {thumbnailPreview ? (
                    <>
                      <img src={thumbnailPreview} alt="Thumbnail" />
                      <span className="remove-btn" onClick={removeThumbnailHandler}>
                        Remove
                      </span>
                    </>
                  ) : (
                    <>
                      <label htmlFor="thumbnail" className="upload-btn">
                        Upload
                      </label>
                      <input
                        type="file"
                        id="thumbnail"
                        accept="image/*"
                        onChange={handleThumbnailChange}
                      />
                    </>
                  )}
                </div>
              </Col>
              <Col xs={24}>
                <label htmlFor="title">Content</label>
                <DocEditor value={content} setValue={setContentHandler} />
                <div className="action-wrapper">
                  <button type="submit">
                    {" "}
                    {isLoading ? <BeatLoader color="#000" size={8} /> : "Submit"}
                  </button>
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Card>
    </div>
  );
}

export default BlogForm;
